import { useRef, useState, useEffect } from 'react';

/**
 * Observes a widget container's pixel width via ResizeObserver.
 * Attach `ref` to the outermost element of the widget.
 * Returns `width` (px) and convenience size-tier booleans.
 */
export function useWidgetSize() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(300);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Measure immediately on mount
    setWidth(el.offsetWidth);

    const observer = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (w > 0) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return {
    ref,
    width,
    isXS: width < 160,   // very narrow — stacked, compact
    isSM: width < 220,   // narrow
    isMD: width < 320,   // medium (default look)
    isLG: width >= 320,  // wide — can show more
    isXL: width >= 480,  // extra wide
  };
}
